// Design → code. Auto-layout frames become flexbox, everything else is absolutely positioned.
import type { Document } from './document.ts';
import type { Effect, NodeData, NodeId, Paint } from './types.ts';
import { colorToCss, blendCss } from './util.ts';
import { textStyle, applyCase, paragraphFormats, truncatedLines } from './text.ts';
import { isAutoLayout } from './layout.ts';
import { cornerRadii, nodeOutline, pathToSvg, sideWeights } from './geometry.ts';
import { cssVariableName, resolveVariable } from './tokens.ts';
import { connectSnippet } from './codeconnect.ts';
import { generateNativeCode } from './codegenNative.ts';

type Css = Record<string, string | number>;

const px = (v: number) => `${Math.round(v * 100) / 100}px`;

function paintCss(p: Paint): string | null {
  if (p.visible === false) return null;
  if (p.type === 'SOLID') return colorToCss(p.color, p.opacity ?? 1);
  if (p.type === 'GRADIENT_LINEAR') {
    const from = p.from ?? { x: 0.5, y: 0 };
    const to = p.to ?? { x: 0.5, y: 1 };
    const angle = (Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI + 90;
    return `linear-gradient(${Math.round(angle)}deg, ${p.stops.map((s) => `${colorToCss(s.color, p.opacity ?? 1)} ${Math.round(s.position * 100)}%`).join(', ')})`;
  }
  if (p.type === 'GRADIENT_ANGULAR') {
    const from = p.from ?? { x: 0.5, y: 0.5 };
    const to = p.to ?? { x: 1, y: 0.5 };
    const angle = (Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI + 90;
    return `conic-gradient(from ${Math.round(angle)}deg at ${Math.round(from.x * 100)}% ${Math.round(from.y * 100)}%, ${p.stops.map((s) => `${colorToCss(s.color, p.opacity ?? 1)} ${Math.round(s.position * 100)}%`).join(', ')})`;
  }
  // CSS has no diamond gradient: the closest is a radial one
  if (p.type === 'GRADIENT_RADIAL' || p.type === 'GRADIENT_DIAMOND') return `radial-gradient(${p.type === 'GRADIENT_DIAMOND' ? 'closest-side, ' : ''}${p.stops.map((s) => `${colorToCss(s.color, p.opacity ?? 1)} ${Math.round(s.position * 100)}%`).join(', ')})`;
  if (p.type === 'IMAGE') return `url(${JSON.stringify(p.src)}) center / ${p.scaleMode === 'FIT' ? 'contain' : p.scaleMode === 'STRETCH' ? '100% 100%' : 'cover'} no-repeat`;
  return null;
}

function shadowCss(effects: Effect[]): string | null {
  const parts = effects
    .filter((e) => e.visible !== false && (e.type === 'DROP_SHADOW' || e.type === 'INNER_SHADOW'))
    .map((e) => {
      if (e.type !== 'DROP_SHADOW' && e.type !== 'INNER_SHADOW') return '';
      return `${e.type === 'INNER_SHADOW' ? 'inset ' : ''}${px(e.offset.x)} ${px(e.offset.y)} ${px(e.radius)} ${px(e.spread ?? 0)} ${colorToCss(e.color)}`;
    });
  return parts.length ? parts.join(', ') : null;
}

export interface CodegenOptions {
  /** 'html' gives a standalone document; 'react' a component; 'css' just the selection's styles; 'swiftui' / 'compose' a native view. */
  format: 'html' | 'react' | 'css' | 'swiftui' | 'compose';
  componentName?: string;
}

interface Ctx {
  doc: Document;
  classes: Map<string, Css>;
  counter: number;
  /** variables the code refers to: custom property → value */
  tokens: Map<string, string>;
  /** imports for connected code components (React) */
  imports: Set<string>;
}

function nodeCss(ctx: Ctx, n: NodeData, parent: NodeData | undefined, isRoot: boolean): Css {
  const css: Css = {};
  const inFlow = parent && isAutoLayout(parent) && n.layoutPositioning !== 'ABSOLUTE';
  if (!isRoot && !inFlow) {
    css.position = 'absolute';
    css.left = px(n.x);
    css.top = px(n.y);
  } else if (isRoot) css.position = 'relative';
  if (n.rotation) css.transform = `rotate(${Math.round(n.rotation * 100) / 100}deg)`;
  const hugW = n.layoutSizingH === 'HUG' || (n.type === 'TEXT' && n.textAutoResize === 'WIDTH_AND_HEIGHT');
  const hugH = n.layoutSizingV === 'HUG' || (n.type === 'TEXT' && n.textAutoResize !== 'NONE');
  const inGrid = inFlow && parent?.layoutMode === 'GRID';
  if (inGrid) {
    const c = n.gridColumn !== undefined ? `${n.gridColumn + 1}` : 'auto';
    const r = n.gridRow !== undefined ? `${n.gridRow + 1}` : 'auto';
    if (n.gridColumn !== undefined || n.gridColumnSpan) css.gridColumn = `${c}${n.gridColumnSpan && n.gridColumnSpan > 1 ? ` / span ${n.gridColumnSpan}` : ''}`;
    if (n.gridRow !== undefined || n.gridRowSpan) css.gridRow = `${r}${n.gridRowSpan && n.gridRowSpan > 1 ? ` / span ${n.gridRowSpan}` : ''}`;
  }
  if (inGrid) {
    // grid cells: fill = stretch to the cell, otherwise the layer's own size
    if (n.layoutSizingH === 'FILL') css.justifySelf = 'stretch';
    else if (!hugW) css.width = px(n.width);
    if (n.layoutSizingV === 'FILL') css.alignSelf = 'stretch';
    else if (!hugH) css.height = px(n.height);
  } else if (inFlow && n.layoutSizingH === 'FILL') {
    if (parent!.layoutMode === 'HORIZONTAL') css.flex = '1 1 0';
    else css.alignSelf = 'stretch';
  } else if (!hugW) css.width = px(n.width);
  if (inGrid) {
    /* sized above */
  } else if (inFlow && n.layoutSizingV === 'FILL') {
    if (parent!.layoutMode === 'VERTICAL') css.flex = '1 1 0';
    else css.alignSelf = 'stretch';
  } else if (!hugH) css.height = px(n.height);
  if (n.opacity < 1) css.opacity = Math.round(n.opacity * 100) / 100;
  const bm = blendCss(n.blendMode);
  if (bm) css.mixBlendMode = bm === 'lighter' ? 'plus-lighter' : bm;
  if (!n.visible) css.display = 'none';

  if (n.type !== 'TEXT') {
    const bgs = n.fills.map(paintCss).filter(Boolean) as string[];
    if (bgs.length === 1 && !bgs[0].includes('gradient') && !bgs[0].startsWith('url')) css.background = bgs[0];
    else if (bgs.length) css.background = [...bgs].reverse().join(', ');
    const stroke = n.strokes.find((p) => p.visible !== false);
    if (stroke && n.strokeWeight > 0) {
      const c = paintCss(stroke) ?? '#000';
      const style = n.strokeDash?.length ? 'dashed' : 'solid';
      const sides = sideWeights(n);
      if (sides) {
        // per-side widths become border widths (CSS draws them inside the box)
        css.borderStyle = style;
        css.borderColor = c;
        css.borderWidth = sides.map(px).join(' ');
        css.boxSizing = 'border-box';
      } else if (n.strokeAlign === 'OUTSIDE') css.outline = `${px(n.strokeWeight)} ${style} ${c}`;
      else {
        css.border = `${px(n.strokeWeight)} ${style} ${c}`;
        css.boxSizing = 'border-box';
      }
    }
    const r = cornerRadii(n);
    if (n.type === 'ELLIPSE') css.borderRadius = '50%';
    else if (r.some(Boolean)) css.borderRadius = r.every((v) => v === r[0]) ? px(r[0]) : r.map(px).join(' ');
    if ((n.type === 'FRAME' || n.type === 'COMPONENT' || n.type === 'INSTANCE') && n.clipsContent) css.overflow = 'hidden';
    const sh = shadowCss(n.effects);
    if (sh) css.boxShadow = sh;
  } else {
    const st = textStyle(n);
    const fill = n.fills.find((p) => p.visible !== false);
    if (fill) css.color = paintCss(fill) ?? '#000';
    css.fontFamily = `${JSON.stringify(st.fontFamily)}, sans-serif`;
    css.fontSize = px(st.fontSize);
    css.fontWeight = st.fontWeight;
    if (st.italic) css.fontStyle = 'italic';
    css.lineHeight = px(st.lineHeight);
    if (st.letterSpacing) css.letterSpacing = px(st.letterSpacing);
    if (st.caps !== 'NORMAL') css.fontVariantCaps = st.caps === 'ALL_SMALL_CAPS' ? 'all-small-caps' : 'small-caps';
    if (st.features.tnum) css.fontVariantNumeric = 'tabular-nums';
    if (st.features.liga === false) css.fontVariantLigatures = 'none';
    if (st.featureCss) css.fontFeatureSettings = st.featureCss;
    if (st.textAlign !== 'LEFT') css.textAlign = st.textAlign === 'JUSTIFIED' ? 'justify' : st.textAlign.toLowerCase();
    if (st.decoration !== 'NONE') css.textDecoration = st.decoration === 'UNDERLINE' ? 'underline' : 'line-through';
    css.whiteSpace = n.textAutoResize === 'WIDTH_AND_HEIGHT' ? 'pre' : 'pre-wrap';
    css.margin = 0;
    const clamp = truncatedLines(n);
    if (clamp === 1) Object.assign(css, { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' });
    else if (Number.isFinite(clamp)) Object.assign(css, { display: '-webkit-box', WebkitLineClamp: clamp, WebkitBoxOrient: 'vertical', overflow: 'hidden' });
    const sh = n.effects.find((e) => e.type === 'DROP_SHADOW' && e.visible !== false);
    if (sh && sh.type === 'DROP_SHADOW') css.textShadow = `${px(sh.offset.x)} ${px(sh.offset.y)} ${px(sh.radius)} ${colorToCss(sh.color)}`;
  }
  const blur = n.effects.find((e) => e.type === 'LAYER_BLUR' && e.visible !== false);
  if (blur) css.filter = `blur(${px((blur as { radius: number }).radius / 2)})`;
  const bgBlur = n.effects.find((e) => e.type === 'BACKGROUND_BLUR' && e.visible !== false);
  if (bgBlur) css.backdropFilter = `blur(${px((bgBlur as { radius: number }).radius / 2)})`;

  if (isAutoLayout(n) && n.layoutMode === 'GRID') {
    css.display = 'grid';
    const tracks = (t: { type: string; value?: number }[] | undefined, dflt: string) => (t?.length ? t.map((x) => (x.type === 'FIXED' ? px(x.value ?? 100) : x.type === 'HUG' ? 'auto' : `${x.value ?? 1}fr`)).join(' ') : dflt);
    css.gridTemplateColumns = tracks(n.gridColumns, '1fr 1fr');
    if (n.gridRows?.length) css.gridTemplateRows = tracks(n.gridRows, '');
    const cg = n.itemSpacing ?? 0,
      rg = n.counterAxisSpacing ?? cg;
    if (cg || rg) css.gap = rg === cg ? px(cg) : `${px(rg)} ${px(cg)}`;
    const pad = [n.paddingTop ?? 0, n.paddingRight ?? 0, n.paddingBottom ?? 0, n.paddingLeft ?? 0];
    if (pad.some(Boolean)) css.padding = pad.every((v) => v === pad[0]) ? px(pad[0]) : pad.map(px).join(' ');
    const ji = { MIN: 'start', CENTER: 'center', MAX: 'end', SPACE_BETWEEN: 'start' }[n.primaryAxisAlign ?? 'MIN'];
    const ai = { MIN: 'start', CENTER: 'center', MAX: 'end', STRETCH: 'stretch' }[n.counterAxisAlign ?? 'MIN'];
    if (ji !== 'start') css.justifyItems = ji;
    if (ai !== 'start') css.alignItems = ai;
    css.boxSizing = 'border-box';
  } else if (isAutoLayout(n)) {
    css.display = 'flex';
    css.flexDirection = n.layoutMode === 'HORIZONTAL' ? 'row' : 'column';
    if (n.layoutWrap) css.flexWrap = 'wrap';
    if (n.itemSpacing) css.gap = n.layoutWrap && n.counterAxisSpacing !== undefined ? `${px(n.counterAxisSpacing)} ${px(n.itemSpacing)}` : px(n.itemSpacing);
    const pad = [n.paddingTop ?? 0, n.paddingRight ?? 0, n.paddingBottom ?? 0, n.paddingLeft ?? 0];
    if (pad.some(Boolean)) css.padding = pad.every((v) => v === pad[0]) ? px(pad[0]) : pad.map(px).join(' ');
    const jc = { MIN: 'flex-start', CENTER: 'center', MAX: 'flex-end', SPACE_BETWEEN: 'space-between' }[n.primaryAxisAlign ?? 'MIN'];
    if (jc !== 'flex-start') css.justifyContent = jc;
    const ai = { MIN: 'flex-start', CENTER: 'center', MAX: 'flex-end', STRETCH: 'stretch' }[n.counterAxisAlign ?? 'MIN'];
    css.alignItems = ai;
    css.boxSizing = 'border-box';
  }
  if (!isRoot && !inFlow) void ctx;
  if (inFlow) css.flexShrink = 0;
  if (inFlow && (n.layoutSizingH === 'FILL' || n.layoutSizingV === 'FILL')) delete css.flexShrink;
  // children positioned absolutely need a positioned parent
  if (!isAutoLayout(n) && ['FRAME', 'GROUP', 'COMPONENT', 'INSTANCE', 'SECTION'].includes(n.type) && !css.position) css.position = 'relative';
  useTokens(ctx, n, css);
  return css;
}

/** Bound variables show up in code as custom properties: `var(--color-primary, #0d99ff)`. */
function useTokens(ctx: Ctx, n: NodeData, css: Css) {
  const b = n.boundVariables;
  if (!b) return;
  const ref = (prop: string, literal: string | number | undefined) => {
    const v = ctx.doc.get(b[prop]);
    if (!v || literal === undefined) return literal;
    const name = cssVariableName(v);
    const value = resolveVariable(ctx.doc, v.id, n.id);
    const lit = typeof literal === 'number' ? px(literal) : literal;
    ctx.tokens.set(name, typeof value === 'number' ? px(value) : value && typeof value === 'object' && 'r' in value ? colorToCss(value) : lit);
    return `var(${name}, ${lit})`;
  };
  const swap = (prop: string, key: keyof Css & string) => {
    if (b[prop] && css[key] !== undefined) css[key] = ref(prop, css[key] as string | number)!;
  };
  if (n.type === 'TEXT') swap('fill', 'color');
  else if (b.fill && typeof css.background === 'string' && !/gradient|url\(/.test(css.background)) swap('fill', 'background');
  swap('textColor', 'color');
  if (b.stroke) {
    if (css.borderColor) swap('stroke', 'borderColor');
    else for (const key of ['border', 'outline'] as const) if (typeof css[key] === 'string') css[key] = (css[key] as string).replace(/(\S+)$/, (c) => String(ref('stroke', c)));
  }
  if (b.cornerRadius && typeof css.borderRadius === 'string' && !css.borderRadius.includes(' ')) swap('cornerRadius', 'borderRadius');
  if (b.itemSpacing && typeof css.gap === 'string' && !css.gap.includes(' ')) swap('itemSpacing', 'gap');
  if (css.padding !== undefined && ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'].some((p) => b[p])) {
    css.padding = (['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'] as const).map((p) => (b[p] ? ref(p, n[p] ?? 0) : px(n[p] ?? 0))).join(' ');
  }
  swap('opacity', 'opacity');
  swap('fontSize', 'fontSize');
  swap('lineHeight', 'lineHeight');
  swap('letterSpacing', 'letterSpacing');
  swap('width', 'width');
  swap('height', 'height');
}

function kebab(k: string) {
  return k.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
}

function cssText(css: Css, indent = '  ') {
  return Object.entries(css)
    .map(([k, v]) => `${indent}${kebab(k)}: ${typeof v === 'number' && !['opacity', 'fontWeight', 'flexShrink', 'zIndex', 'WebkitLineClamp'].includes(k) ? px(v) : v};`)
    .join('\n');
}

function className(n: NodeData, ctx: Ctx): string {
  const base = (n.name || n.type).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 24) || 'layer';
  let name = /^[a-z]/.test(base) ? base : `n-${base}`;
  if (ctx.classes.has(name)) name = `${name}-${++ctx.counter}`;
  return name;
}

function escapeHtml(s: string) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function svgFor(n: NodeData): string {
  const outline = nodeOutline(n);
  const d = outline ? pathToSvg(outline) : '';
  const fill = n.fills.find((p) => p.visible !== false);
  const stroke = n.strokes.find((p) => p.visible !== false);
  return `<svg width="${Math.max(1, n.width)}" height="${Math.max(1, n.height)}" viewBox="0 0 ${Math.max(1, n.width)} ${Math.max(1, n.height)}" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="${d}"${fill ? ` fill="${paintCss(fill)}"` : ''}${stroke ? ` stroke="${paintCss(stroke)}" stroke-width="${n.strokeWeight}"` : ''}/></svg>`;
}

function renderHtml(ctx: Ctx, id: NodeId, isRoot: boolean, depth: number, jsx: boolean): string {
  const n = ctx.doc.must(id);
  const parent = ctx.doc.get(n.parent);
  // Code Connect: a connected instance is the team's real component, not markup
  const connected = jsx && n.type === 'INSTANCE' ? connectSnippet(ctx.doc, id) : null;
  if (connected) {
    connected.imports.forEach((i) => ctx.imports.add(i));
    return `${'  '.repeat(depth)}${connected.jsx}`;
  }
  const css = nodeCss(ctx, n, parent, isRoot);
  const pad = '  '.repeat(depth);
  const cls = className(n, ctx);
  const attr = jsx ? 'className' : 'class';
  const vectorish = ['VECTOR', 'STAR', 'POLYGON', 'LINE'].includes(n.type) || (n.type === 'SHAPE_WITH_TEXT' && !['SQUARE', 'ELLIPSE', 'ROUNDED_RECTANGLE'].includes(n.shape ?? 'SQUARE'));
  if (vectorish) {
    delete css.background;
    delete css.border;
  }
  ctx.classes.set(cls, css);
  if (n.type === 'TEXT') {
    const chars = applyCase(n.characters ?? '', textStyle(n).textCase);
    let text = escapeHtml(chars);
    if (n.textRuns?.length) {
      let html = '';
      let pos = 0;
      for (const r of [...n.textRuns].sort((a, b) => a.start - b.start)) {
        if (r.start > pos) html += escapeHtml(chars.slice(pos, r.start));
        const st: string[] = [];
        if (r.fontWeight) st.push(`font-weight: ${r.fontWeight}`);
        if (r.italic) st.push('font-style: italic');
        if (r.fontSize) st.push(`font-size: ${r.fontSize}px`);
        if (r.color) st.push(`color: ${colorToCss(r.color)}`);
        if (r.textDecoration && r.textDecoration !== 'NONE') st.push(`text-decoration: ${r.textDecoration === 'UNDERLINE' ? 'underline' : 'line-through'}`);
        const inner = escapeHtml(chars.slice(r.start, r.end));
        const styleAttr = st.length ? (jsx ? ` style={{${st.map((d) => { const [k, v] = d.split(': '); return `${k.replace(/-([a-z])/g, (_, c) => c.toUpperCase())}: '${v}'`; }).join(', ')}}}` : ` style="${st.join('; ')}"`) : '';
        html += r.link ? `<a href="${escapeHtml(r.link)}"${styleAttr}>${inner}</a>` : `<span${styleAttr}>${inner}</span>`;
        pos = r.end;
      }
      if (pos < chars.length) html += escapeHtml(chars.slice(pos));
      text = html;
    }
    const formats = paragraphFormats(n);
    if (formats) {
      // nested <ul>/<ol> per paragraph; plain paragraphs between lists become <p>
      const paras = chars.split('\n');
      const out: string[] = [];
      const open: { tag: string; level: number }[] = [];
      const close = (level: number) => {
        while (open.length && open[open.length - 1].level >= level) {
          const o = open.pop()!;
          out.push(`${pad}${'  '.repeat(open.length + 1)}</${o.tag}>`);
        }
      };
      paras.forEach((t, i) => {
        const f = formats[i];
        if (!f.list) {
          close(0);
          if (t) out.push(`${pad}  <p${f.level ? ` style=${jsx ? `{{ marginLeft: ${f.level * 24} }}` : `"margin-left: ${f.level * 24}px"`}` : ''}>${escapeHtml(t)}</p>`);
          return;
        }
        const tag = f.list === 'NUMBER' ? 'ol' : 'ul';
        close(f.level + 1);
        const top = open[open.length - 1];
        if (top && top.level === f.level && top.tag !== tag) close(f.level);
        if (!open.length || open[open.length - 1].level < f.level) {
          out.push(`${pad}${'  '.repeat(open.length + 1)}<${tag}>`);
          open.push({ tag, level: f.level });
        }
        out.push(`${pad}${'  '.repeat(open.length + 1)}<li>${escapeHtml(t)}</li>`);
      });
      close(0);
      return `${pad}<div ${attr}="${cls}">\n${out.join('\n')}\n${pad}</div>`;
    }
    const tag = (n.fontSize ?? 16) >= 28 ? 'h2' : 'p';
    // JSX: escape literal braces in plain text (rich spans already carry JSX style objects)
    const body = jsx ? (n.textRuns?.length ? text : text.replace(/[{}]/g, (m) => `{'${m}'}`)).replace(/\n/g, '<br />') : text.replace(/\n/g, '<br>');
    return `${pad}<${tag} ${attr}="${cls}">${body}</${tag}>`;
  }
  if (vectorish) return `${pad}<div ${attr}="${cls}">${svgFor(n)}</div>`;
  const kids = ctx.doc.children(id).filter((k) => k.type !== 'CONNECTOR');
  const label = n.characters ? `\n${pad}  <span>${escapeHtml(n.characters)}</span>` : '';
  const inner = kids.map((k) => renderHtml(ctx, k.id, false, depth + 1, jsx)).join('\n');
  const tag = n.name.toLowerCase().includes('button') ? 'button' : 'div';
  if (!inner && !label) return `${pad}<${tag} ${attr}="${cls}"></${tag}>`;
  return `${pad}<${tag} ${attr}="${cls}">${label}${inner ? `\n${inner}` : ''}\n${pad}</${tag}>`;
}

export function generateCode(doc: Document, ids: NodeId[], opts: CodegenOptions): string {
  if (opts.format === 'swiftui' || opts.format === 'compose') return generateNativeCode(doc, ids, { format: opts.format, componentName: opts.componentName });
  const ctx: Ctx = { doc, classes: new Map(), counter: 0, tokens: new Map(), imports: new Set() };
  const jsx = opts.format === 'react';
  const body = ids.map((id) => renderHtml(ctx, id, true, jsx ? 2 : 1, jsx)).join('\n');
  const rules = [...ctx.classes].map(([name, css]) => `.${name} {\n${cssText(css)}\n}`);
  // the tokens the selection uses, with their values in its current mode
  if (ctx.tokens.size) rules.unshift(`:root {\n${[...ctx.tokens].map(([k, v]) => `  ${k}: ${v};`).join('\n')}\n}`);
  const styles = rules.join('\n');
  if (opts.format === 'css') return styles;
  if (opts.format === 'html') {
    return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap" rel="stylesheet" />
<style>
* { box-sizing: border-box; }
body { margin: 0; font-family: Inter, sans-serif; }
button { font: inherit; border: 0; cursor: pointer; }
${styles}
</style>
</head>
<body>
${body}
</body>
</html>
`;
  }
  const name = (opts.componentName ?? doc.get(ids[0])?.name ?? 'Design').replace(/[^A-Za-z0-9]+(.)?/g, (_, c: string) => (c ? c.toUpperCase() : '')).replace(/^./, (c) => c.toUpperCase()) || 'Design';
  return `// Generated by Vellum
${[...ctx.imports].map((i) => `${i}\n`).join('')}const css = \`
${styles}
\`;

export function ${/^[A-Z]/.test(name) ? name : 'Design' + name}() {
  return (
    <>
      <style>{css}</style>
${body}
    </>
  );
}
`;
}
